import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SpinnerComponent } from '@fc-shared/ui/spinner/spinner.component';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { AppIconsType } from '@fc-shared/ui/icon/icons';
import {
  NgButtonDirective,
  ButtonVariant,
  ButtonSize,
  ButtonColor,
  ButtonIconPosition,
} from './ng-button.directive';

@Component({
  selector: 'ng-button',
  host: {
    '[style.pointer-events]': 'disabled() || loading() ? "none" : null',
    '[attr.aria-disabled]': 'disabled() || loading() ? "true" : null',
  },
  template: `
    <button
      ngButton
      #btn="ngButton"
      [variant]="variant()"
      [size]="size()"
      [color]="resolvedColor()"
      [iconPosition]="resolvedIconPosition()"
      [loading]="loading()"
      [disabled]="disabled() || loading()"
      [focused]="focused()"
      [type]="type()"
      [attr.aria-label]="ariaLabel()"
    >
      <!-- Loading Spinner -->
      @if (loading()) {
        <div class="absolute inset-0 flex items-center justify-center z-10">
          <fc-spinner
            [color]="btn.spinnerColor()"
            [size]="btn.spinnerSize()"
          ></fc-spinner>
        </div>
      }

      <!-- Button Content -->
      <div
        class="flex items-center justify-center w-full transition-opacity duration-200"
        [class.gap-4]="size() === 'sm'"
        [class.gap-8]="size() === 'md'"
        [class.opacity-0]="loading()"
      >
        @if (resolvedIcon() && resolvedIconPosition() === 'only') {
          <fc-icon [icon]="resolvedIcon()!" [size]="20"></fc-icon>
        }
        @if (resolvedIcon() && resolvedIconPosition() === 'start') {
          <fc-icon [icon]="resolvedIcon()!" [size]="20"></fc-icon>
        }

        <ng-content></ng-content>

        @if (resolvedIcon() && resolvedIconPosition() === 'end') {
          <fc-icon [icon]="resolvedIcon()!" [size]="20"></fc-icon>
        }
      </div>
    </button>
  `,
  styles: `
    :host(.w-full) {
      display: block;
      width: 100%;
    }

    :host(.w-full) button {
      width: 100%;
    }

    button {
      text-transform: capitalize;
    }
  `,
  imports: [CommonModule, SpinnerComponent, IconComponent, NgButtonDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NgButtonComponent {
  readonly variant = input<ButtonVariant>('filled');
  readonly size = input<ButtonSize>('md');
  readonly color = input<ButtonColor | undefined>(undefined);
  readonly loading = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  readonly focused = input<boolean>(false);
  readonly ariaLabel = input<string>();

  // Component-specific inputs
  readonly icon = input<AppIconsType | null>(null);
  readonly iconPosition = input<ButtonIconPosition>('start');
  readonly iconOnly = input<boolean>(false);
  readonly type = input<'button' | 'submit' | 'reset'>('button');

  readonly resolvedColor = computed((): ButtonColor => {
    return (
      this.color() ??
      (this.resolvedIconPosition() === 'only' ? 'secondary' : 'primary')
    );
  });

  readonly resolvedIcon = computed((): AppIconsType | null => this.icon());

  readonly resolvedIconPosition = computed((): ButtonIconPosition => {
    if (this.variant() === 'icon') {
      return 'only';
    }

    if (!this.resolvedIcon()) {
      return 'none';
    }

    if (this.iconOnly()) {
      return 'only';
    }

    return this.iconPosition();
  });
}
